<template>
  <div class="card-refunds-management">
    <!-- Header Section (theme-aligned with UserManage.vue) -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-top-row">
          <div class="title-section">
            <h1 class="main-title">Card Payment Refunds</h1>
            <p class="subtitle">Monitor and review refund transactions</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="refund-content">
      <v-card class="elevation-4" style="border-radius: 12px; overflow: hidden">
        <!-- Filters / Actions Row -->
        <v-card-title class="py-3">
          <v-layout align-center justify-space-between wrap>
            <v-layout align-center wrap class="filters-row">
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Search (order code, provider ref)"
                hide-details
                clearable
                outline
                flat
                class="mr-2 mb-2"
                @keyup.enter="onSearch"
              />

              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                label="Status"
                hide-details
                clearable
                outline
                flat
                class="mr-2 mb-2"
                @change="fetchList"
              />
            </v-layout>
            <v-layout align-center>
              <v-btn icon color="primary" @click="fetchList">
                <v-icon color="black">refresh</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                @click="openCreateDialog"
                class="btn-style"
                style="color: black"
                :icon="$vuetify.breakpoint.xs"
              >
                <v-icon
                  :class="{ 'mr-2': !$vuetify.breakpoint.xs }"
                  color="black"
                  >add</v-icon
                >
                <span v-if="!$vuetify.breakpoint.xs">Create Refund</span>
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-progress-linear v-if="loading" color="primary" indeterminate />

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="refunds"
          :loading="loading"
          :search="internalSearch"
          :total-items="total"
          :pagination.sync="pagination"
          :rows-per-page-items="[10, 25, 50]"
          class="elevation-1"
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.id }}</td>
            <td>
              <v-chip
                :color="statusColor(props.item.refund_status)"
                text-color="white"
                small
                class="type-badge-enhanced"
              >
                {{ props.item.refund_status.toUpperCase() }}
              </v-chip>
            </td>
            <td class="text-no-wrap">
              {{ formatCurrency(props.item.amount) }}
            </td>
            <td class="text-no-wrap">{{ props.item.flashpay_charge_id }}</td>
            <td class="text-no-wrap">{{ props.item.order_code }}</td>
            <td class="text-no-wrap">
              {{ props.item.flashpay_refund_id || "-" }}
            </td>
            <td class="text-no-wrap">
              {{ formatDate(props.item.expected_refund_date) }}
            </td>
            <td class="text-no-wrap">
              {{ formatDate(props.item.created_at) }}
            </td>
            <td class="text-no-wrap">
              {{ formatDate(props.item.updated_at) }}
            </td>
            <td class="text-xs-center">
              <v-btn icon small @click="openDetail(props.item)">
                <v-icon>visibility</v-icon>
              </v-btn>
            </td>
          </template>
          <template slot="no-data">
            <div v-if="loading" class="pa-5 text-xs-center">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
              <div class="mt-2 grey--text text--darken-1">
                Loading refunds...
              </div>
            </div>
            <v-alert type="info" :value="true" class="ma-4" v-else>
              No refunds found.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <!-- Detail Dialog -->
      <v-dialog v-model="detailDialog" max-width="840px" persistent scrollable>
        <v-card class="refund-detail-dialog">
          <!-- Dialog Header styled like NewUser.vue -->
          <v-card-title class="dialog-header">
            <div class="header-content">
              <div class="icon-wrapper">
                <v-icon color="white" size="24">receipt</v-icon>
              </div>
              <div class="header-text">
                <h2 class="dialog-title">Refund Detail</h2>
                <p class="dialog-subtitle">
                  Provider, transaction, and timeline information
                </p>
              </div>
            </div>
            <v-btn icon @click="detailDialog = false" class="close-btn">
              <v-icon color="white">close</v-icon>
            </v-btn>
          </v-card-title>

          <!-- Dialog Content -->
          <v-card-text class="dialog-content">
            <!-- Inline loader while preparing details -->
            <div v-if="detailInlineLoading" class="pa-4 text-xs-center">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
              <div class="mt-2 grey--text text--darken-1">
                Loading detail...
              </div>
            </div>
            <div v-else>
              <!-- Section: Basic Info -->
              <div class="form-section">
                <div class="section-header">
                  <v-icon color="primary" class="mr-2">info</v-icon>
                  <h3 class="section-title">Basic Information</h3>
                </div>
                <v-layout wrap>
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>ID:</strong> {{ detailRecord.id }}</v-flex
                  >
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Order Code:</strong>
                    {{ detailRecord.order_code }}</v-flex
                  >
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Charge ID:</strong>
                    {{ detailRecord.flashpay_charge_id }}</v-flex
                  >
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Provider Refund ID:</strong>
                    {{ detailRecord.flashpay_refund_id || "-" }}</v-flex
                  >
                </v-layout>
              </div>

              <!-- Section: Amount & Status -->
              <div class="form-section">
                <div class="section-header">
                  <v-icon color="primary" class="mr-2">monetization_on</v-icon>
                  <h3 class="section-title">Amount & Status</h3>
                </div>
                <v-layout wrap>
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Amount:</strong>
                    {{ formatCurrency(detailRecord.amount) }}</v-flex
                  >
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Status:</strong>

                    <v-chip
                      :color="statusColor(detailRecord.refund_status)"
                      text-color="white"
                      small
                      class="type-badge-enhanced"
                    >
                      {{ detailRecord.refund_status?.toUpperCase() }}
                    </v-chip>
                  </v-flex>
                </v-layout>
              </div>

              <!-- Section: Timeline -->
              <div class="form-section">
                <div class="section-header">
                  <v-icon color="primary" class="mr-2">schedule</v-icon>
                  <h3 class="section-title">Timeline</h3>
                </div>
                <v-layout wrap>
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Expected Refund:</strong>
                    {{ formatDate(detailRecord.expected_refund_date) }}</v-flex
                  >
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Created:</strong>
                    {{ formatDate(detailRecord.created_at) }}</v-flex
                  >
                  <v-flex xs12 sm6 class="py-1"
                    ><strong>Updated:</strong>
                    {{ formatDate(detailRecord.updated_at) }}</v-flex
                  >
                </v-layout>
              </div>

              <!-- Section: Reason -->
              <div class="form-section">
                <div class="section-header">
                  <v-icon color="primary" class="mr-2">help_outline</v-icon>
                  <h3 class="section-title">Reason</h3>
                </div>
                <div>{{ detailRecord.reason || "-" }}</div>
              </div>

              <!-- Section: Provider Information (structured) -->
              <div class="form-section" v-if="providerObj">
                <div class="section-header">
                  <v-icon color="primary" class="mr-2"
                    >assignment_turned_in</v-icon
                  >
                  <h3 class="section-title">Provider Information</h3>
                </div>
                <v-layout wrap>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>OK:</strong>
                    {{
                      providerObj.ok === true
                        ? "Yes"
                        : providerObj.ok === false
                        ? "No"
                        : "-"
                    }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Provider State:</strong>
                    <v-chip
                      :color="statusColor(providerObj.state)"
                      small
                      text-color="white"
                    >
                      {{ providerObj.state || "-" }}
                    </v-chip>
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Trade Status:</strong>
                    <v-chip
                      :color="providerTradeStatusColor"
                      small
                      text-color="white"
                    >
                      {{ providerTradeStatusLabel }}
                    </v-chip>
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Currency:</strong>
                    {{ providerDataSection.cur || "-" }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Refund Amount:</strong>
                    {{ formatCurrency(providerDataSection.refundAmount / 100) }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Trade No:</strong>
                    {{ providerDataSection.tradeNo || "-" }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Out Trade No:</strong>
                    {{ providerDataSection.outTradeNo || "-" }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Trade Time:</strong>
                    {{ formatDate(providerDataSection.tradeTime) }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Complete Time:</strong>
                    {{ formatDate(providerDataSection.completeTime) }}
                  </v-flex>
                  <v-flex xs12 sm6 class="py-1">
                    <strong>Return Code:</strong>
                    {{ providerDataSection.retCode || "-" }}
                  </v-flex>
                  <v-flex xs12 sm12 class="py-1">
                    <strong>Return Message:</strong>
                    {{ providerDataSection.retMessage || "-" }}
                  </v-flex>
                </v-layout>
              </div>

              <!-- Section: Provider Response -->
              <div class="form-section" v-if="detailProvider !== null">
                <div class="section-header">
                  <v-icon color="primary" class="mr-2">dns</v-icon>
                  <h3 class="section-title">Provider Response</h3>
                </div>
                <div>
                  <span v-if="typeof detailProvider === 'string'">{{
                    detailProvider
                  }}</span>
                  <pre
                    v-else
                    class="grey lighten-4 pa-3"
                    style="white-space: pre-wrap"
                    >{{ pretty(detailProvider) }}</pre
                  >
                </div>
              </div>
            </div>
          </v-card-text>

          <!-- Footer Actions -->
          <v-card-actions class="dialog-footer">
            <v-spacer></v-spacer>
            <v-btn flat color="primary" @click="detailDialog = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Loader while fetching detail -->
      <v-dialog v-model="detailLoading" persistent width="320">
        <v-card class="pa-4 text-xs-center">
          <v-card-text>
            <v-progress-circular
              indeterminate
              color="primary"
              size="40"
            ></v-progress-circular>
            <div class="mt-3 grey--text text--darken-1">
              Fetching refund detail...
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Create Refund Dialog -->
      <v-dialog v-model="createDialog" max-width="880px" persistent scrollable>
        <v-card class="refund-detail-dialog">
          <v-card-title class="dialog-header">
            <div class="header-content">
              <div class="icon-wrapper">
                <v-icon color="white" size="24">payment</v-icon>
              </div>
              <div class="header-text">
                <h2 class="dialog-title">Create Refund</h2>
                <p class="dialog-subtitle">
                  Search payment by order code and submit refund
                </p>
              </div>
            </div>
            <v-btn icon @click="closeCreateDialog" class="close-btn">
              <v-icon color="white">close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="dialog-content">
            <!-- Search Section -->
            <div class="form-section">
              <div class="section-header">
                <v-icon color="primary" class="mr-2">search</v-icon>
                <h3 class="section-title">Search by Order Code</h3>
              </div>
              <v-layout wrap align-center>
                <v-flex xs12 sm8 class="pr-sm-2">
                  <v-text-field
                    v-model="searchOrderCode"
                    label="Order code (e.g. MEIXXXX)"
                    clearable
                    :disabled="creating"
                    @keyup.enter="doSearch"
                  />
                </v-flex>
                <v-flex xs12 sm4 class="pl-sm-2">
                  <v-btn
                    color="primary"
                    @click="doSearch"
                    class="btn-style"
                    style="color: black"
                    :icon="$vuetify.breakpoint.xs"
                    :loading="searchLoading"
                    :disabled="!searchOrderCode"
                  >
                    <v-icon
                      :class="{ 'mr-2': !$vuetify.breakpoint.xs }"
                      color="black"
                      >search</v-icon
                    >
                    <span v-if="!$vuetify.breakpoint.xs">Search</span>
                  </v-btn>
                </v-flex>
              </v-layout>
              <div v-if="paymentResults.length" class="mt-3">
                <v-data-table
                  :headers="paymentHeaders"
                  :items="paymentResults"
                  hide-actions
                  class="elevation-0"
                >
                  <template slot="items" slot-scope="props">
                    <td class="text-no-wrap">{{ props.item.order_code }}</td>
                    <td class="text-no-wrap">{{ props.item.payment_id }}</td>
                    <td class="text-no-wrap">
                      {{ props.item.provider_reference }}
                    </td>
                    <td class="text-no-wrap">
                      {{ formatCurrency(props.item.amount) }}
                    </td>
                    <td class="text-no-wrap">
                      {{ formatCurrency(props.item.refunded_amount || 0) }}
                    </td>
                    <td class="text-no-wrap">{{ props.item.status }}</td>
                    <td class="text-no-wrap">
                      {{ formatDate(props.item.payment_completed_time) }}
                    </td>
                    <td class="text-xs-center">
                      <v-btn
                        small
                        color="primary"
                        flat
                        @click="selectPayment(props.item)"
                      >
                        Select
                      </v-btn>
                    </td>
                  </template>
                  <template slot="no-data">
                    <v-alert type="info" :value="true" class="ma-0"
                      >No payments found for this order code.</v-alert
                    >
                  </template>
                </v-data-table>
              </div>
            </div>

            <!-- Selected Payment & Refund Form -->
            <div class="form-section" v-if="selectedPayment">
              <div class="section-header">
                <v-icon color="primary" class="mr-2">assignment</v-icon>
                <h3 class="section-title">Refund Details</h3>
              </div>
              <v-layout wrap>
                <v-flex xs12 sm6 class="py-1"
                  ><strong>Order Code:</strong>
                  {{ selectedPayment.order_code }}</v-flex
                >
                <v-flex xs12 sm6 class="py-1"
                  ><strong>Payment ID:</strong>
                  {{ selectedPayment.payment_id }}</v-flex
                >
                <v-flex xs12 sm6 class="py-1"
                  ><strong>Provider Ref:</strong>
                  {{ selectedPayment.provider_reference }}</v-flex
                >
                <v-flex xs12 sm6 class="py-1"
                  ><strong>Paid Amount:</strong>
                  {{ formatCurrency(selectedPayment.amount) }}</v-flex
                >
                <v-flex xs12 sm6 class="py-1"
                  ><strong>Already Refunded:</strong>
                  {{
                    formatCurrency(selectedPayment.refunded_amount || 0)
                  }}</v-flex
                >
                <v-flex xs12 sm6 class="py-1"
                  ><strong>Max Refundable:</strong>
                  {{ formatCurrency(maxRefundAmount) }}</v-flex
                >
              </v-layout>

              <v-layout wrap class="mt-2">
                <v-flex xs12 sm6 class="pr-sm-2">
                  <v-text-field
                    v-model.number="refundAmount"
                    type="number"
                    label="Refund amount (THB)"
                    :rules="refundAmountRules"
                    :disabled="creating"
                    min="0"
                    step="0.01"
                    class="pr-2"
                  />
                </v-flex>
                <v-flex xs12 sm6 class="pl-sm-2">
                  <v-text-field
                    v-model="refundReason"
                    label="Reason (optional)"
                    :disabled="creating"
                  />
                </v-flex>
              </v-layout>
            </div>
          </v-card-text>

          <v-card-actions class="dialog-footer">
            <v-spacer />
            <v-btn
              flat
              color="red"
              @click="closeCreateDialog"
              :disabled="creating"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              :loading="creating"
              :disabled="!canSubmitRefund"
              @click="submitRefund"
              style="color: black"
              class="btn-style"
            >
              Submit refund
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import moment from "moment";
import Swal from "sweetalert2";

export default {
  name: "FlashpayCardPaymentRefund",
  data() {
    return {
      loading: false,
      detailLoading: false,
      detailInlineLoading: false,
      search: "",
      // debounced search handler (initialized in created)
      debouncedSearch: null,
      statusFilter: null,
      pagination: {
        sortBy: "created_at",
        descending: true,
        rowsPerPage: 10,
        page: 1,
      },
      detailDialog: false,
      createDialog: false,
      searchOrderCode: "",
      searchLoading: false,
      paymentResults: [],
      selectedPayment: null,
      refundAmount: null,
      refundReason: "",
      creating: false,
      headers: [
        { text: "ID", value: "id", align: "start" },
        { text: "Status", value: "refund_status", align: "center" },
        { text: "Amount", value: "amount", align: "center" },
        { text: "Charge ID", value: "flashpay_charge_id", align: "center" },
        { text: "Order Code", value: "order_code", align: "center" },
        {
          text: "Provider Refund ID",
          value: "flashpay_refund_id",
          align: "center",
        },
        {
          text: "Expected Refund",
          value: "expected_refund_date",
          align: "center",
        },
        { text: "Created", value: "created_at", align: "center" },
        { text: "Updated", value: "updated_at", align: "center" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      statusOptions: [
        { text: "All", value: null },
        { text: "Pending", value: "pending" },
        { text: "Submitted", value: "submitted" },
      ],
      paymentHeaders: [
        { text: "Order Code", value: "order_code" },
        { text: "Payment ID", value: "payment_id" },
        { text: "Provider Ref", value: "provider_reference" },
        { text: "Amount", value: "amount" },
        { text: "Refunded", value: "refunded_amount" },
        { text: "Status", value: "status" },
        { text: "Completed", value: "payment_completed_time" },
        { text: "", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    ...mapState("fetchOrderDetailBack", {
      refunds: (state) => state.card_refunds,
      total: (state) => state.card_refunds_count,
      detail: (state) => state.card_refund_detail,
    }),
    internalSearch() {
      // lightweight client-side search field used for quick filtering; server search uses onSearch()
      return this.search;
    },
    detailRecord() {
      // Support either { transaction, provider } or direct transaction in store
      const d = this.detail || {};
      return d.transaction ? d.transaction : d;
    },
    detailProvider() {
      const d = this.detail || {};
      return typeof d.provider === "undefined" ? null : d.provider;
    },
    providerObj() {
      const p = this.detailProvider;
      if (!p) return null;
      if (typeof p === "object") return p;
      try {
        return JSON.parse(p);
      } catch (e) {
        return null;
      }
    },
    providerDataSection() {
      if (!this.providerObj) return {};
      // Prefer top-level data object; fallback to raw.data if needed
      return (
        this.providerObj.data ||
        (this.providerObj.raw && this.providerObj.raw.data) ||
        {}
      );
    },
    providerTradeStatusCode() {
      if (!this.providerObj) return null;
      // tradeStatus may appear at top-level or within data
      return (
        this.providerObj.tradeStatus ||
        (this.providerObj.data && this.providerObj.data.tradeStatus) ||
        null
      );
    },
    providerTradeStatusLabel() {
      const code = Number(this.providerTradeStatusCode);
      if (code === 2) return "2 (processing)";
      if (code === 3) return "3 (success)";
      if (code === 4) return "4 (failed)";
      return code ? `${code} (unknown)` : "-";
    },
    providerTradeStatusColor() {
      const code = Number(this.providerTradeStatusCode);
      if (code === 2) return "blue darken-2";
      if (code === 3) return "green darken-2";
      if (code === 4) return "red darken-2";
      return "grey darken-1";
    },
    maxRefundAmount() {
      if (!this.selectedPayment) return 0;
      const paid = Number(this.selectedPayment.amount || 0);
      const refunded = Number(this.selectedPayment.refunded_amount || 0);
      return Math.max(0, paid - refunded);
    },
    refundAmountRules() {
      return [
        (v) =>
          (v !== null && v !== undefined && v !== "") || "Amount is required",
        (v) => Number(v) >= 0 || "Amount must be >= 0",
        (v) =>
          Number(v) <= this.maxRefundAmount ||
          `Max ${this.formatCurrency(this.maxRefundAmount)}`,
      ];
    },
    canSubmitRefund() {
      if (!this.selectedPayment) return false;
      const v = Number(this.refundAmount);

      return (
        !this.creating &&
        !isNaN(v) &&
        parseFloat(v) > 0 &&
        parseFloat(v) <= this.maxRefundAmount
      );
    },
  },
  watch: {
    "pagination.page": "fetchList",
    "pagination.rowsPerPage": "fetchList",
    // Debounce server-side search by order_code while typing
    search() {
      if (this.debouncedSearch) this.debouncedSearch();
    },
    selectedPayment(newVal) {
      if (newVal) {
        // default to full remaining amount
        const remaining = this.maxRefundAmount;
        this.refundAmount = remaining > 0 ? Number(remaining.toFixed(2)) : 0;
      } else {
        this.refundAmount = null;
      }
    },
  },
  created() {
    // set up debounced search (400ms)
    this.debouncedSearch = this.debounce(() => {
      this.pagination.page = 1;
      this.fetchList();
    }, 400);
    this.fetchList();
  },
  mounted() {
    this.initializePage();
  },
  methods: {
    ...mapActions("fetchOrderDetailBack", [
      "fetchCardRefunds",
      "fetchCardRefundDetail",
      "searchCardPayments",
      "createCardRefund",
    ]),
    // Simple debounce utility (no external deps)
    debounce(fn, delay = 300) {
      let t = null;
      return (...args) => {
        if (t) clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), delay);
      };
    },
    async fetchList() {
      this.loading = true;
      const page = this.pagination.page;
      const page_size = this.pagination.rowsPerPage;
      const status = this.statusFilter || undefined;
      const order_code =
        this.search && this.search.trim() ? this.search.trim() : undefined;
      // optional server-side filters: status, order_code
      await this.fetchCardRefunds({ page, page_size, status, order_code });
      this.loading = false;
    },
    onSearch() {
      // If backend supports search, adapt here. For now, rely on client-side v-data-table search binding.
      this.pagination.page = 1;
      this.fetchList();
    },
    openCreateDialog() {
      this.createDialog = true;
      this.searchOrderCode = "";
      this.paymentResults = [];
      this.selectedPayment = null;
      this.refundAmount = null;
      this.refundReason = "";
    },
    closeCreateDialog() {
      if (this.creating) return;
      this.createDialog = false;
    },
    async doSearch() {
      if (!this.searchOrderCode) return;
      this.searchLoading = true;
      this.paymentResults = [];
      this.selectedPayment = null;
      const res = await this.searchCardPayments({
        order_code: this.searchOrderCode,
        limit: 50,
        offset: 0,
      });
      if (res !== false) {
        this.paymentResults = Array.isArray(res) ? res : [];
        if (this.paymentResults.length === 1) {
          this.selectPayment(this.paymentResults[0]);
        }
      }
      this.searchLoading = false;
    },
    selectPayment(item) {
      this.selectedPayment = item;
    },
    async submitRefund() {
      if (!this.canSubmitRefund) return;
      this.creating = true;
      const payload = {
        // Both provided; backend accepts either
        order_code: this.selectedPayment.order_code,
        payment_id: this.selectedPayment.payment_id,
        amount: Number(this.refundAmount),
        reason:
          this.refundReason ||
          `Refund via backoffice for ${this.selectedPayment.order_code}`,
      };
      const res = await this.createCardRefund(payload);
      this.creating = false;
      if (res !== false) {
        Swal.fire({
          icon: "success",
          title: "Refund queued",
          text: `Transaction #${res.transaction_id} created`,
          timer: 2000,
          showConfirmButton: false,
        });
        this.createDialog = false;
        this.fetchList();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed to create refund",
          text: "Please verify order/payment and try again.",
        });
      }
    },
    statusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "pending":
          return "orange darken-2";
        case "submitted":
          return "blue darken-2";
        case "succeeded":
          return "green darken-2";
        case "failed":
          return "red darken-2";
        default:
          return "grey darken-1";
      }
    },
    formatCurrency(amount) {
      if (amount == null) return "-";
      const v = Number(amount);
      try {
        return new Intl.NumberFormat("th-TH", {
          style: "currency",
          currency: "THB",
        }).format(v);
      } catch (e) {
        return `${v.toFixed(2)} THB`;
      }
    },
    formatDate(iso) {
      if (!iso) return "-";
      const m = moment(iso);
      if (!m.isValid()) return String(iso);
      return m.format("DD-MM-YYYY HH:mm:ss");
    },
    pretty(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },
    async openDetail(item) {
      // Show a blocking loader while fetching details
      this.detailLoading = true;
      this.detailInlineLoading = true;
      const ok = await this.fetchCardRefundDetail(item.id);
      this.detailLoading = false;
      if (ok !== false) {
        this.detailDialog = true;
        // Small delay to allow dialog mount before removing inline loader
        this.$nextTick(() => {
          this.detailInlineLoading = false;
        });
      } else {
        this.detailInlineLoading = false;
        Swal.fire({
          icon: "error",
          title: "Unable to load detail",
          text: "Please try again.",
        });
      }
    },
    initializePage() {
      const path = [
        {
          text: "Select province",
          disabled: false,
          href: "/weserve_admin_login/select/",
        },
        {
          text: "Card payment refunds",
          disabled: true,
          href: "/weserve_admin_login/control/-/financial/card-refunds",
        },
      ];
      this.$store.commit("backOffice/saveRecentRoute", path);
    },
  },
};
</script>

<style scoped>
.card-refunds-management {
  min-height: 100vh;
  background: transparent;
  padding: 20px;
}

.header-section {
  padding: 2rem 1rem;
  margin-bottom: 1rem;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
}

.header-top-row {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 2rem;
}

.title-section {
  flex: 1;
  text-align: left;
}

.main-title {
  font-size: 3rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  text-align: left;
  text-shadow: 2px 2px 4px rgba(117, 117, 117, 0.5);
}

.subtitle {
  font-size: 1.2rem;
  color: #7f8c8d;
  text-align: left;
  margin-bottom: 0;
}

.refund-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem 2rem;
}

.filters-row {
  gap: 8px;
}

@media (max-width: 768px) {
  .card-refunds-management {
    padding: 10px;
  }

  .header-top-row {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .title-section {
    text-align: center;
    margin-bottom: 1rem;
  }

  .main-title {
    font-size: 2rem;
    text-align: center;
  }

  .subtitle {
    text-align: center;
  }
}

/* Dialog styles adapted from NewUser.vue for visual consistency */
.refund-detail-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #141414 0%, #9e9e9e 100%);
  color: white;
  padding: 24px;
  position: relative;
}

.header-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
  margin-right: 16px;
  backdrop-filter: blur(10px);
}

.header-text {
  flex: 1;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: white;
}

.dialog-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
  color: white;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.05);
}

.dialog-content {
  padding: 32px 24px;
  background: #fafafa;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.form-section:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.btn-style {
  border-radius: 8px;
  text-transform: none;
  color: black;
}

.type-badge-enhanced {
  border-radius: 30px !important;
  font-weight: 700 !important;
  text-transform: capitalize !important;
  letter-spacing: 0.8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  padding: 6px 16px !important;
  height: 32px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative !important;
  overflow: hidden !important;
}

.type-badge-enhanced:hover {
  transform: translateY(-2px) scale(1.05) !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3) !important;
}

.type-badge-enhanced:before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.6s;
}

.type-badge-enhanced:hover:before {
  left: 100%;
}

.type-badge-enhanced .v-chip__content {
  display: flex !important;
  align-items: center !important;
  font-size: 0.75rem !important;
  z-index: 1;
  position: relative;
}
</style>